import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';
import { BaseAPI } from 'src/providers/base-api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// ============================================================================
// Types (match backend DTOs)
// ============================================================================

export interface PublicTenantConfig {
  tenantId: number;
  tenantName: string;
  logoAssetId: string;
  googleOAuthEnabled: boolean;
  mobileNumberLogin: boolean;
  employeeIdLoginEnabled: boolean;
  forgotPasswordEnabled: boolean;
  geoFencingRequired: boolean;
  autoLoginDomains: string[];
}

export interface AuthenticatedTenantConfig {
  shiftHandlingEnabled: boolean;
  salaryDetailsEnabled: boolean;
  allowSupervisorMarkingAttendance: boolean;
  geoFencingRequired: boolean;
  hideEmployeeEmail: boolean;
  quickLinks: {
    wfh: boolean;
    permission: boolean;
    emergency: boolean;
  };
  enabledModules: string[];
  enableClientUserCreate: boolean;
  canEditAttendance: boolean;
}

/** Merged feature flags interface used by all consumers. */
export interface TenantFeatures {
  // From public config (available pre-login)
  tenantId: number;
  tenantName: string;
  logoAssetId: string;
  mobileNumberLogin: boolean;
  employeeIdLoginEnabled: boolean;
  geoFencingRequired: boolean;
  googleOAuthEnabled: boolean;
  forgotPasswordEnabled: boolean;
  autoLoginDomains: string[];

  // From authenticated config (available post-login)
  shiftHandlingEnabled: boolean;
  salaryDetailsEnabled: boolean;
  allowSupervisorMarkingAttendance: boolean;
  hideEmployeeEmail: boolean;
  quickLinksWFHEnabled: boolean;
  quickLinksPermissionEnabled: boolean;
  quickLinksEmergencyEnabled: boolean;
  enabledModules: string[];
  enableClientUserCreate: boolean;
  canEditAttendance: boolean;
}

interface TenantConfigState {
  publicConfig: PublicTenantConfig | null;
  authenticatedConfig: AuthenticatedTenantConfig | null;
  publicLoading: boolean;
  authenticatedLoading: boolean;
  publicLoaded: boolean;
  authenticatedLoaded: boolean;
  error: string | null;
}

/** Safe defaults matching the old "default" tenant config. */
export const DEFAULT_TENANT_FEATURES: TenantFeatures = {
  tenantId: 0,
  tenantName: '',
  logoAssetId: '',
  mobileNumberLogin: false,
  employeeIdLoginEnabled: false,
  geoFencingRequired: false,
  googleOAuthEnabled: true,
  forgotPasswordEnabled: true,
  autoLoginDomains: [],
  shiftHandlingEnabled: true,
  salaryDetailsEnabled: false,
  allowSupervisorMarkingAttendance: false,
  hideEmployeeEmail: false,
  quickLinksWFHEnabled: true,
  quickLinksPermissionEnabled: true,
  quickLinksEmergencyEnabled: true,
  enabledModules: [],
  enableClientUserCreate: false,
  canEditAttendance: false,
};

// ============================================================================
// Initial State
// ============================================================================

const initialState: TenantConfigState = {
  publicConfig: null,
  authenticatedConfig: null,
  publicLoading: false,
  authenticatedLoading: false,
  publicLoaded: false,
  authenticatedLoaded: false,
  error: null,
};

// ============================================================================
// Async Thunks
// ============================================================================

/** Fetch public config (no auth). Called on login page mount.
 *  If slug is provided (from /t/<slug>/login URL), fetches config for that specific tenant.
 */
export const fetchPublicTenantConfig = createAsyncThunk<
  PublicTenantConfig,
  string | undefined,
  { rejectValue: string }
>('tenantConfig/fetchPublic', async (slug, { rejectWithValue }) => {
  try {
    const params = slug ? `?slug=${encodeURIComponent(slug)}` : '';
    const response = await axios.get(`${API_BASE_URL}/public/tenant-config${params}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenant config');
  }
});

/** Fetch authenticated config (requires JWT). Called after login. */
export const fetchAuthenticatedTenantConfig = createAsyncThunk<
  AuthenticatedTenantConfig,
  void,
  { rejectValue: string }
>('tenantConfig/fetchAuthenticated', async (_, { rejectWithValue }) => {
  try {
    const userDetail = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
    const token = userDetail?.access_token;
    if (!token) {
      return rejectWithValue('No auth token');
    }

    const response = await axios.get(`${API_BASE_URL}/tenants/config`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-Id': BaseAPI.getTenantIdFromToken() ?? '',
      },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenant config');
  }
});

// ============================================================================
// Slice
// ============================================================================

const tenantConfigSlice = createSlice({
  name: 'tenantConfig',
  initialState,
  reducers: {
    clearTenantConfig: (state) => {
      state.publicConfig = null;
      state.authenticatedConfig = null;
      state.publicLoaded = false;
      state.authenticatedLoaded = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicTenantConfig.pending, (state) => {
        state.publicLoading = true;
        state.error = null;
      })
      .addCase(fetchPublicTenantConfig.fulfilled, (state, action) => {
        state.publicLoading = false;
        state.publicLoaded = true;
        state.publicConfig = action.payload;
      })
      .addCase(fetchPublicTenantConfig.rejected, (state, action) => {
        state.publicLoading = false;
        state.publicLoaded = true;
        state.error = action.payload || 'Failed';
      })
      .addCase(fetchAuthenticatedTenantConfig.pending, (state) => {
        state.authenticatedLoading = true;
      })
      .addCase(fetchAuthenticatedTenantConfig.fulfilled, (state, action) => {
        state.authenticatedLoading = false;
        state.authenticatedLoaded = true;
        state.authenticatedConfig = action.payload;
      })
      .addCase(fetchAuthenticatedTenantConfig.rejected, (state, action) => {
        state.authenticatedLoading = false;
        state.authenticatedLoaded = true;
        state.error = action.payload || 'Failed';
      });
  },
});

// ============================================================================
// Exports
// ============================================================================

export const { clearTenantConfig } = tenantConfigSlice.actions;
export default tenantConfigSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

/** Memoized merged selector: only recomputes when publicConfig or authenticatedConfig changes. */
export const selectTenantFeatures = createSelector(
  [(state: { tenantConfig: TenantConfigState }) => state.tenantConfig.publicConfig,
   (state: { tenantConfig: TenantConfigState }) => state.tenantConfig.authenticatedConfig],
  (pub, auth): TenantFeatures => ({
    tenantId: pub?.tenantId ?? DEFAULT_TENANT_FEATURES.tenantId,
    tenantName: pub?.tenantName ?? DEFAULT_TENANT_FEATURES.tenantName,
    logoAssetId: pub?.logoAssetId ?? DEFAULT_TENANT_FEATURES.logoAssetId,
    mobileNumberLogin: pub?.mobileNumberLogin ?? DEFAULT_TENANT_FEATURES.mobileNumberLogin,
    employeeIdLoginEnabled: pub?.employeeIdLoginEnabled ?? DEFAULT_TENANT_FEATURES.employeeIdLoginEnabled,
    geoFencingRequired: pub?.geoFencingRequired ?? DEFAULT_TENANT_FEATURES.geoFencingRequired,
    googleOAuthEnabled: pub?.googleOAuthEnabled ?? DEFAULT_TENANT_FEATURES.googleOAuthEnabled,
    forgotPasswordEnabled: pub?.forgotPasswordEnabled ?? DEFAULT_TENANT_FEATURES.forgotPasswordEnabled,
    autoLoginDomains: pub?.autoLoginDomains ?? DEFAULT_TENANT_FEATURES.autoLoginDomains,
    shiftHandlingEnabled: auth?.shiftHandlingEnabled ?? DEFAULT_TENANT_FEATURES.shiftHandlingEnabled,
    salaryDetailsEnabled: auth?.salaryDetailsEnabled ?? DEFAULT_TENANT_FEATURES.salaryDetailsEnabled,
    allowSupervisorMarkingAttendance: auth?.allowSupervisorMarkingAttendance ?? DEFAULT_TENANT_FEATURES.allowSupervisorMarkingAttendance,
    hideEmployeeEmail: auth?.hideEmployeeEmail ?? DEFAULT_TENANT_FEATURES.hideEmployeeEmail,
    quickLinksWFHEnabled: auth?.quickLinks?.wfh ?? DEFAULT_TENANT_FEATURES.quickLinksWFHEnabled,
    quickLinksPermissionEnabled: auth?.quickLinks?.permission ?? DEFAULT_TENANT_FEATURES.quickLinksPermissionEnabled,
    quickLinksEmergencyEnabled: auth?.quickLinks?.emergency ?? DEFAULT_TENANT_FEATURES.quickLinksEmergencyEnabled,
    enabledModules: auth?.enabledModules ?? DEFAULT_TENANT_FEATURES.enabledModules,
    enableClientUserCreate: auth?.enableClientUserCreate ?? DEFAULT_TENANT_FEATURES.enableClientUserCreate,
    canEditAttendance: auth?.canEditAttendance ?? DEFAULT_TENANT_FEATURES.canEditAttendance,
  })
);

export const selectTenantConfigLoading = (state: { tenantConfig: TenantConfigState }) =>
  state.tenantConfig.publicLoading || state.tenantConfig.authenticatedLoading;

export const selectAuthenticatedConfigLoaded = (state: { tenantConfig: TenantConfigState }) =>
  state.tenantConfig.authenticatedLoaded;
